import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InfoIcon } from 'lucide-react';

interface InfoTooltipProps {
  content: string;
  position?: 'top' | 'right' | 'bottom' | 'left';
  /** Optional text trigger (e.g. the top bar's "Data through Oct 3"). Absent,
   *  the trigger is the info icon, as before. */
  children?: React.ReactNode;
}

const GAP = 8;
const WIDTH = 256; // w-64
const EDGE = 8;

/**
 * The bubble renders in a portal on document.body with fixed positioning.
 * Rendered inline, it lived inside its card's stacking context (cards lift
 * with a transform on hover), so later siblings — charts, date pills — painted
 * over it no matter its z-index.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  position = 'top',
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isVisible || !buttonRef.current || !tooltipRef.current) return;
    const b = buttonRef.current.getBoundingClientRect();
    const t = tooltipRef.current.getBoundingClientRect();
    let top: number;
    let left: number;
    switch (position) {
      case 'bottom':
        top = b.bottom + GAP;
        left = b.left + b.width / 2 - t.width / 2;
        break;
      case 'left':
        top = b.top + b.height / 2 - t.height / 2;
        left = b.left - GAP - t.width;
        break;
      case 'right':
        top = b.top + b.height / 2 - t.height / 2;
        left = b.right + GAP;
        break;
      default:
        top = b.top - GAP - t.height;
        left = b.left + b.width / 2 - t.width / 2;
    }
    // Keep the bubble inside the viewport.
    left = Math.min(Math.max(left, EDGE), window.innerWidth - t.width - EDGE);
    top = Math.min(Math.max(top, EDGE), window.innerHeight - t.height - EDGE);
    setCoords({ top, left });
  }, [isVisible, position, content]);

  const show = () => setIsVisible(true);
  const hide = () => {
    setIsVisible(false);
    setCoords(null);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        className={children ? 'cursor-default text-left focus:outline-none' : 'text-gray-400 hover:text-gray-600 transition-colors focus:outline-none'}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={() => (isVisible ? hide() : show())}
        aria-label={children ? undefined : 'More information'}
      >
        {children ?? <InfoIcon className="h-4 w-4" />}
      </button>

      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              width: WIDTH,
              // Measured first, then placed: hidden until positioned.
              visibility: coords ? 'visible' : 'hidden',
            }}
            className="z-[1000] p-3 text-sm bg-brand-navy text-white rounded-lg shadow-lg pointer-events-none"
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default InfoTooltip;
